import { t } from '@lingui/core/macro';
import { useMutation } from '@tanstack/react-query';

import { participantProfilesControllerVerifyEmploymentEmailCode } from '@/api/core-api';

export const useVerifyWorkEmailCode = () => {
  return useMutation({
    mutationFn: async ({
      profileId,
      code,
    }: {
      profileId: string;
      code: string;
    }) => {
      const response =
        await participantProfilesControllerVerifyEmploymentEmailCode({
          path: { id: profileId },
          body: { code },
        });

      if (response.status === 409) {
        throw new Error(
          t`That work email is already verified on another account.`,
        );
      }
      if (response.status === 429) {
        throw new Error(
          t`Too many incorrect attempts. Send yourself a new code to try again.`,
        );
      }
      if (response.error) {
        // 400 covers both a wrong code and no code on file. The server
        // distinguishes them in its message, so prefer that over a generic
        // string when one came back.
        throw new Error(
          extractServerMessage(response.error) ??
            t`That code isn't right. Check it and try again.`,
        );
      }

      return response.data;
    },
  });
};

function extractServerMessage(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.length > 0
  ) {
    return error.message;
  }

  return undefined;
}
